import {
  lazy,
  StrictMode,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  MutableRefObject,
  ReactNode,
} from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import { ErrorBoundary } from 'react-error-boundary'
// import AmadeusPL from './routes/Amadeus'

import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import './index.css'
// import { ConsoleLogger, AsyncDuckDB } from '@duckdb/duckdb-wasm'
import { Provider } from 'react-redux'
import { store } from './store.ts'
// import duckdb_wasm_eh from '@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url'
// import eh_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url'
import { AuthProvider } from './AuthContext.tsx'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { CookiesProvider } from 'react-cookie'
import {
  createTheme,
  ThemeProvider,
  useMediaQuery,
  CssBaseline,
} from '@mui/material'
const Entry = lazy(() => import('./MockEntry.tsx'))
const Compare = lazy(() => import('./routes/Compare/Compare.tsx'))
const FernPLTable = lazy(() => import('./routes/PJM/Fern.tsx'))
const NewMap = lazy(() => import('./routes/NewMap/Map.tsx'))
const Map = lazy(() => import('./routes/Map/Map.tsx'))
const Stream = lazy(() => import('./Stream.tsx'))
const ModelMain = lazy(() => import('./routes/Prism/ModelMain.tsx'))
const Capex = lazy(() => import('./routes/Capex/Capex.tsx'))
const ProjectOverview = lazy(() => import('./routes/Prism/ProjectOverview.tsx'))
const PrismCompare = lazy(() => import('./routes/Prism/Compare/Compare.tsx'))
const DocList = lazy(() => import('./routes/Prism/DocList.tsx'))
const PPAInput = lazy(() => import('./routes/Prism/RevUtils/PPAInput.tsx'))
const MerchantInput = lazy(
  () => import('./routes/Prism/RevUtils/MerchantInput.tsx')
)
const ProjectManager = lazy(() => import('./routes/Prism/ProjectManager.tsx'))
const IssueTracker = lazy(() => import('./IssueTracker/IssueTracker.tsx'))

type ErrorFallbackProps = {
  error: Error
  resetErrorBoundary: () => void
  autoRetryCountRef?: MutableRefObject<number>
  maxAutoRetries?: number
}

function ErrorFallback({
  error,
  resetErrorBoundary,
  autoRetryCountRef,
  maxAutoRetries = 0,
}: ErrorFallbackProps) {
  const retriesUsed = autoRetryCountRef?.current ?? 0
  const shouldAutoRetry =
    autoRetryCountRef != null && retriesUsed < maxAutoRetries

  useEffect(() => {
    if (!shouldAutoRetry || autoRetryCountRef == null) {
      return
    }
    autoRetryCountRef.current += 1
    const timer = window.setTimeout(() => {
      resetErrorBoundary()
    }, 250)
    return () => window.clearTimeout(timer)
  }, [autoRetryCountRef, resetErrorBoundary, shouldAutoRetry])

  return (
    <div role="alert" style={{ padding: '20px', border: '2px solid red' }}>
      <h2>React Error Caught:</h2>
      {shouldAutoRetry ? (
        <p>{`Retrying automatically (${retriesUsed + 1}/${maxAutoRetries})...`}</p>
      ) : null}
      <details>
        <summary>Error Details (click to expand)</summary>
        <pre style={{ whiteSpace: 'pre-wrap' }}>
          <strong>Message:</strong> {error.message}
        </pre>
        <pre style={{ whiteSpace: 'pre-wrap' }}>
          <strong>Stack:</strong> {error.stack}
        </pre>
        <pre style={{ whiteSpace: 'pre-wrap' }}>
          <strong>Name:</strong> {error.name}
        </pre>
      </details>
      <button
        onClick={() => {
          if (autoRetryCountRef != null) {
            autoRetryCountRef.current = 0
          }
          resetErrorBoundary()
        }}
      >
        Try again
      </button>
    </div>
  )
}

type ResetRetryCounterProps = {
  autoRetryCountRef: MutableRefObject<number>
  children: ReactNode
}

function ResetRetryCounter({
  autoRetryCountRef,
  children,
}: ResetRetryCounterProps) {
  useEffect(() => {
    autoRetryCountRef.current = 0
  }, [autoRetryCountRef])

  return <>{children}</>
}

function AutoRetryErrorBoundary({ children }: { children: ReactNode }) {
  const autoRetryCountRef = useRef(0)
  const renderFallback = useCallback(
    ({ error, resetErrorBoundary }: ErrorFallbackProps) => (
      <ErrorFallback
        error={error}
        resetErrorBoundary={resetErrorBoundary}
        autoRetryCountRef={autoRetryCountRef}
        maxAutoRetries={2}
      />
    ),
    []
  )

  return (
    <ErrorBoundary FallbackComponent={renderFallback}>
      <ResetRetryCounter autoRetryCountRef={autoRetryCountRef}>
        {children}
      </ResetRetryCounter>
    </ErrorBoundary>
  )
}

// const DataView = lazy(() => import('./routes/Prism/DataView'))
const queryClient = new QueryClient()

function withErrorBoundary(Component: React.ComponentType) {
  return (
    <AutoRetryErrorBoundary>
      <Component />
    </AutoRetryErrorBoundary>
  )
}
const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: '/issues',
        element: <IssueTracker />,
      },
      {
        path: '/issues/new',
        element: <IssueTracker />,
      },
      {
        path: '/issues/:issueId',
        element: <IssueTracker />,
      },
      {
        path: 'plants/fern',
        element: <FernPLTable />,
      },
      {
        path: 'compare',
        element: <Compare />,
      },
      {
        path: 'map',
        element: <Map />,
      },
      {
        path: 'newmap',
        element: <NewMap />,
      },
      {
        path: 'stream',
        element: <Stream />,
      },
      {
        path: 'prism',
        element: <ModelMain />,
      },
      {
        path: 'prism/project_manager',
        element: withErrorBoundary(ProjectManager),
      },
      {
        path: 'prism/merchant',
        element: withErrorBoundary(MerchantInput),
      },
      {
        path: 'prism/PPA',
        element: withErrorBoundary(PPAInput),
      },
      {
        path: 'prism/:projectId',
        // element: <ProjectOverview />,
        element: withErrorBoundary(ProjectOverview),
      },
      {
        path: 'prism/:projectId/:scenarioId',
        // element: <DocList />,
        element: withErrorBoundary(DocList),
      },
      {
        path: 'prism/compare',
        element: withErrorBoundary(PrismCompare),
      },

      // {
      //   path: 'prism/dataview',
      //   element: withErrorBoundary(DataView),
      // },
      {
        path: 'entry',
        element: <Entry />,
      },
      {
        path: 'capex/*',
        element: <Capex />,
      },
    ],
  },
])

function ThemedApp() {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: prefersDark ? 'dark' : 'light',
          ...(prefersDark
            ? { background: { default: '#2a2a2e', paper: '#1e1e22' } }
            : { background: { default: '#f0f8ff', paper: '#ffffff' } }),
        },
      }),
    [prefersDark]
  )
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RouterProvider router={router} />
    </ThemeProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <AutoRetryErrorBoundary>
    <StrictMode>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <CookiesProvider defaultSetOptions={{ path: '/' }}>
            <AuthProvider>
              <ThemedApp />
            </AuthProvider>
          </CookiesProvider>
        </QueryClientProvider>
      </Provider>
    </StrictMode>
  </AutoRetryErrorBoundary>
)
